import React from "react";
import { Box, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useFormikContext } from "formik";

import type { GoalFormValues } from "./GoalBuilderPanel.types";
import {
  BEHAVIOR_SUBJECT_OPTIONS,
  DOMAIN_OPTIONS,
  GOAL_TYPE_OPTIONS,
  STATUS_OPTIONS,
} from "./GoalBuilderPanel.types";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface FieldGroupProps {
  label: string;
  description?: string;
  required?: boolean;
  children: React.ReactNode;
}

/** Label + optional description + input slot, matching the Figma FieldGroup layout. */
const FieldGroup: React.FC<FieldGroupProps> = ({ label, description, required, children }) => (
  <Stack gap={3}>
    <Box>
      <Typography variant="body2" component="span">
        {label}
      </Typography>
      {required && (
        <Typography component="span" variant="body2" color="error.main">
          {" "}
          *
        </Typography>
      )}
      {description && (
        <Typography variant="caption" color="text.secondary" component="p" lineHeight="140%">
          {description}
        </Typography>
      )}
    </Box>
    {children}
  </Stack>
);

// ---------------------------------------------------------------------------
// GoalFormSection1
// ---------------------------------------------------------------------------

/**
 * Section 1 — Goal information.
 * Reads and writes form state via Formik context — must be rendered inside a
 * Formik provider (GoalBuilderPanel handles this).
 *
 * Fields: behavior_subject, goal_type, title, domain, status, description,
 * general_session_instructions, reinforcement_context, strategy_notes.
 *
 */
const GoalFormSection1: React.FC = () => {
  const { values, errors, touched, handleChange, handleBlur } = useFormikContext<GoalFormValues>();

  return (
    <Stack
      component={"section"}
      data-testid="goal-form-section-1"
      direction={"column"}
      gap={2}
      p={3}
      sx={{
        bgcolor: (theme) => (theme.palette as any).grayscale?.[50] ?? "#F6F7F8",
        borderRadius: 3,
      }}
    >
      {/* Section header */}
      <Box pt={2} sx={{ px: 1 }}>
        <Typography component="h2" variant="subtitle2">
          Section 1 - Goal information
        </Typography>
        <Typography component="p" variant="caption" color="text.secondary" lineHeight="140%">
          Basic information about this goal
        </Typography>
      </Box>

      {/* Fields card */}
      <Stack
        direction={"column"}
        gap={4}
        p={6}
        borderRadius={3}
        bgcolor="background.paper"
        sx={{
          border: "1px solid",
          borderColor: (theme) => (theme.palette as any).grayscale?.[100] ?? "#EAECEF",
        }}
      >
        <FieldGroup
          label="Whose behavior will be measured?"
          description="Select whether the goal measures client or caregiver behavior"
          required
        >
          <Select
            size="small"
            fullWidth
            name="behavior_subject"
            value={values.behavior_subject}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.behavior_subject && Boolean(errors.behavior_subject)}
            aria-label="Whose behavior will be measured"
            displayEmpty
          >
            {BEHAVIOR_SUBJECT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FieldGroup>

        <FieldGroup label="Goal Type" description="Type of goal to create" required>
          <Select
            size="small"
            fullWidth
            name="goal_type"
            value={values.goal_type}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.goal_type && Boolean(errors.goal_type)}
            inputProps={{ "aria-label": "Goal type" }}
            displayEmpty
          >
            {GOAL_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FieldGroup>

        <FieldGroup label="Goal Name" description="Clear, descriptive name for this goal" required>
          <TextField
            size="small"
            fullWidth
            name="title"
            placeholder="e.g., Receptively identify common objects"
            value={values.title}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.title && Boolean(errors.title)}
            helperText={touched.title && errors.title}
            aria-label="Goal name"
          />
        </FieldGroup>

        {/* Domain + Status — two columns */}
        <Stack component="section" direction="row" gap={4}>
          <Box flex={1}>
            <FieldGroup label="Domain" description="Clinical domain for this goal" required>
              <Select
                size="small"
                fullWidth
                name="domain"
                value={values.domain}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.domain && Boolean(errors.domain)}
                aria-label="Clinical domain"
                displayEmpty
                renderValue={(val) =>
                  val ? (
                    DOMAIN_OPTIONS.find((o) => o.value === val)?.label
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      Select domain...
                    </Typography>
                  )
                }
                sx={{ minWidth: 0 }}
              >
                {DOMAIN_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FieldGroup>
          </Box>
          <Box flex={1}>
            <FieldGroup label="Status" description="Current operational state" required>
              <Select
                size="small"
                fullWidth
                name="status"
                value={values.status}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.status && Boolean(errors.status)}
                aria-label="Goal status"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FieldGroup>
          </Box>
        </Stack>

        <FieldGroup
          label="Goal Statement"
          description="Full statement including observable behavior, conditions, and high-level teaching approach notes"
          required
        >
          <TextField
            multiline
            minRows={4}
            fullWidth
            name="description"
            placeholder="e.g., When presented with an array of 3 common objects and given the verbal instruction 'Give me [object],' client will independently hand the correct object..."
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.description && Boolean(errors.description)}
            helperText={touched.description && errors.description}
            aria-label="Goal statement"
          />
        </FieldGroup>

        <FieldGroup
          label="General Session Instructions"
          description="High-level, session-agnostic guidance for implementing this goal across targets"
        >
          <TextField
            multiline
            minRows={4}
            fullWidth
            name="general_session_instructions"
            placeholder="e.g., Always use visual supports; session length 30 min max"
            value={values.general_session_instructions}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-label="General session instructions"
          />
        </FieldGroup>

        <FieldGroup
          label="Reinforcement Context"
          description="Goal-level reinforcement strategy notes"
        >
          <TextField
            multiline
            minRows={4}
            fullWidth
            name="reinforcement_context"
            placeholder="e.g., Preferred reinforcers, satiation considerations, motivating operations"
            value={values.reinforcement_context}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-label="Reinforcement context"
          />
        </FieldGroup>

        <FieldGroup
          label="Strategy Notes"
          description="Clinical insights or special considerations for this goal"
        >
          <TextField
            multiline
            minRows={4}
            fullWidth
            name="strategy_notes"
            placeholder="e.g., Clinical insights, special considerations for this goal"
            value={values.strategy_notes}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-label="Strategy notes"
          />
        </FieldGroup>
      </Stack>
    </Stack>
  );
};

export default GoalFormSection1;
